import { Button, Divider, Intent, Spinner } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { ButtonContainer, FGCustomPanel, FieldGroup, FieldSet, showError, useApiEffect } from "nsitools-react";
import * as React from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import { DogApi, DogFusionDto, ErrorDetails } from "../../../api";
import {
  CancelButton,
  FGSRSHCheckboxInput,
  InformationCard,
  SaveButton,
  SmallFormGenerator,
} from "../../../components";
import { ERoutes } from "../../../ERoutes";
import { useApiService, useSaveApi, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { DogFusion } from "./DogFusion";

export interface IDogFusionPageProps {}

const StyledSpace = styled.div`
  margin-bottom: 1rem;
`;

export const DogFusionPage: React.FunctionComponent<IDogFusionPageProps> = (props) => {
  const api = useApiService(DogApi);
  const { id } = useParams<{ id: string }>();
  const { t, tUnsafe } = useTl();
  const history = useHistory();
  const fusionId2 = window.localStorage.getItem("dogFusionId");

  const [selectLeft, setselectLeft] = React.useState(null);

  /* ========== Getting Data ===========*/
  const [dogComparison, dogLoading] = useApiEffect(() => api.getDogFusionComparisonDogs(+id, +fusionId2));
  const [data, dataLoading] = useApiEffect(() => api.getDogFusion(+id, +fusionId2));

  /* ===================================*/

  const { saving, saveItem } = useSaveApi(
    React.useMemo(
      () => ({
        saveApiFn: async (d: DogFusionDto) => {
          try {
            const result = await api.dogFusion(d);
            history.push(`${ERoutes.dog}/${result}/detail/view`);
          } catch (error) {
            const details: ErrorDetails = JSON.parse(error.response);
            if (details?.errorCode) {
              showError(tUnsafe(`BackendError_${details?.errorCode}`));
            } else {
              showError(t(ETLCodes.Error));
            }
            throw error;
          }
        },
      }),
      [api, history, t, tUnsafe]
    )
  );

  const save = React.useCallback(
    async (formikData) => {
      if (
        formikData.dog1.kennelId !== null &&
        formikData.dog2.kennelId !== null &&
        formikData.dog1.kennelId !== formikData.dog2.kennelId
      ) {
        showError(t(ETLCodes.DogFusionKennelsError));
      } else {
        if (formikData.dogComparison.dog1HasDnaTest && formikData.dogComparison.dog2HasDnaTest) {
          showError(t(ETLCodes.DogFusionDnaTestError));
        } else {
          await saveItem(formikData.fusion);
        }
      }
    },
    [saveItem, t]
  );

  /* ===================================*/

  return dogLoading || dataLoading ? (
    <Spinner />
  ) : (
    <SmallFormGenerator
      initialValues={{
        fusion: data,
        dog1: dogComparison.dog1,
        dog2: dogComparison.dog2,
        dogComparison: dogComparison,
      }}
      onSubmit={save}
      editMode={true}
      loading={dataLoading}
      saving={saving}
      enableDirtyCheck={false}
      dirtyCheckSaveButton={false}
      hideButtons={true}
    >
      <FieldGroup columns={2}>
        <FieldSet
          title={
            <Link target="_blank" to={`${ERoutes.dog}/${dogComparison.dog1.dogId}/detail/view`}>
              {dogComparison.dog1.dogName}
            </Link>
          }
          rightElement={
            <Button
              text={t(ETLCodes.SelectOther)}
              intent={Intent.PRIMARY}
              minimal={false}
              icon={IconNames.SWAP_HORIZONTAL}
              onClick={() => {
                if (selectLeft === null) {
                  setselectLeft(false);
                } else {
                  setselectLeft(!selectLeft);
                }
              }}
            />
          }
        />
        <FieldSet
          title={
            <Link target="_blank" to={`${ERoutes.dog}/${dogComparison.dog2.dogId}/detail/view`}>
              {dogComparison.dog2.dogName}
            </Link>
          }
        />
      </FieldGroup>
      <DogFusion dog1={dogComparison.dog1} dog2={dogComparison.dog2} selectLeft={selectLeft} />
      <InformationCard text={t(ETLCodes.DogFusionChipsAndOwnersInformation)} type="Information" />
      <StyledSpace />

      <Divider />
      <StyledSpace />

      <FieldSet title={t(ETLCodes.Kennels)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput name="dogComparison.dog1HasKennel" label={t(ETLCodes.HasKennel)} readonly />
          <FGSRSHCheckboxInput name="dogComparison.dog2HasKennel" label={t(ETLCodes.HasKennel)} readonly />

          {dogComparison.dog1.kennelId !== null && (
            <div style={{ marginLeft: "4em" }}>
              <Link target="_blank" to={`${ERoutes.kennel}/${dogComparison.dog1.kennelId}/detail/view`}>
                {`${dogComparison.dog1.kennel.name}`}
              </Link>
            </div>
          )}
          {dogComparison.dog2.kennelId !== null && (
            <div style={{ marginLeft: "4em" }}>
              <Link target="_blank" to={`${ERoutes.kennel}/${dogComparison.dog2.kennelId}/detail/view`}>
                {`${dogComparison.dog2.kennel.name}`}
              </Link>
            </div>
          )}
        </FieldGroup>
        {dogComparison.dog1.kennelId !== null &&
          dogComparison.dog2.kennelId !== null &&
          dogComparison.dog2.kennelId !== dogComparison.dog1.kennelId && (
            <InformationCard text={t(ETLCodes.DogFusionKennelsError)} type="Error" />
          )}
        <StyledSpace />
      </FieldSet>
      <FieldSet title={t(ETLCodes.Requests)}>
        {/* TODO: fix this horrible perf in prod
         <FieldGroup columns={2}>
          <FGSRSHCheckboxInput
            name="dogComparison.dog1HasRequests"
            label={t(ETLCodes.HasRequests)}
            readonly
          />
          <FGSRSHCheckboxInput
            name="dogComparison.dog2HasRequests"
            label={t(ETLCodes.HasRequests)}
            readonly
          />
        </FieldGroup> */}
        {dogComparison.dog1HasRequests && dogComparison.dog2HasRequests && (
          <InformationCard text={t(ETLCodes.DogFusionRequestsInformation)} type="Information" />
        )}
        <StyledSpace />
      </FieldSet>
      <FieldSet title={t(ETLCodes.Sport)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput
            name="dogComparison.dog1HasTitles"
            label={t(ETLCodes.HasTitlesAndCompetitions)}
            readonly
          />
          <FGSRSHCheckboxInput
            name="dogComparison.dog2HasTitles"
            label={t(ETLCodes.HasTitlesAndCompetitions)}
            readonly
          />
        </FieldGroup>
        {dogComparison.dog1HasTitles && dogComparison.dog2HasTitles && (
          <InformationCard text={t(ETLCodes.DogFusionSportInformation)} type="Information" />
        )}
        <StyledSpace />
      </FieldSet>
      <FieldSet title={t(ETLCodes.Workbooks)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput name="dogComparison.dog1HasWorkbooks" label={t(ETLCodes.HasWorkbooks)} readonly />
          <FGSRSHCheckboxInput name="dogComparison.dog2HasWorkbooks" label={t(ETLCodes.HasWorkbooks)} readonly />
        </FieldGroup>
        {dogComparison.dog1HasWorkbooks && dogComparison.dog2HasWorkbooks && (
          <InformationCard text={t(ETLCodes.DogFusionWorkbooksInformation)} type="Information" />
        )}
        <StyledSpace />
      </FieldSet>
      <FieldSet title={t(ETLCodes.Dysplasia)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput name="dogComparison.dog1HasDysplasias" label={t(ETLCodes.HasDysplasias)} readonly />
          <FGSRSHCheckboxInput name="dogComparison.dog2HasDysplasias" label={t(ETLCodes.HasDysplasias)} readonly />
        </FieldGroup>
        {dogComparison.dog1HasDysplasias && dogComparison.dog2HasDysplasias && (
          <InformationCard text={t(ETLCodes.DogFusionDysplasiaInformation)} type="Information" />
        )}
        <StyledSpace />
      </FieldSet>
      <FieldSet title={t(ETLCodes.DnaTests)}>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput name="dogComparison.dog1HasDnaTest" label={t(ETLCodes.HasDnaTest)} readonly />
          <FGSRSHCheckboxInput name="dogComparison.dog2HasDnaTest" label={t(ETLCodes.HasDnaTest)} readonly />
        </FieldGroup>

        {dogComparison.dog1HasDnaTest && dogComparison.dog2HasDnaTest && !dogComparison.dnaTestsIdentical && (
          <InformationCard text={t(ETLCodes.DogFusionDnaTestError)} type="Error" />
        )}
        <StyledSpace />
      </FieldSet>
      <FGCustomPanel>
        {(ctx) => (
          <ButtonContainer>
            <div style={{ marginRight: "0.5rem" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.dog}`)} />
            </div>
            <SaveButton minimal={false} loading={saving} onClick={() => save(ctx.formik.values)} />
          </ButtonContainer>
        )}
      </FGCustomPanel>
    </SmallFormGenerator>
  );
};
